import { Injectable } from '@angular/core';
import { UserManager, User, UserManagerSettings, Log } from 'oidc-client';
import { Subject, forkJoin } from 'rxjs';

import { environment } from 'src/environments/environment';
import { UserRoleService } from './user-role.service';
import { UserContext } from '../shared/models/user-context';
import { Operation } from '../shared/models/operation';
import { BusinessOperation } from '../shared/models/business-operation';
import { PpsUtilityService } from './pps-utility.service';
import { LoginChangeModel, LogoutReason } from '../shared/models/login-change-model';
import { LoggingService } from './logging.service';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { format } from 'date-fns';
import { AppStatus } from '../shared/models/app-status';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private _userManager: UserManager;
  private _user: User = null;
  _userContext: UserContext = null;

  private _loginChangeSubject = new Subject<boolean>();
  loginChanged = this._loginChangeSubject.asObservable();

  private _appLoadingSubject = new Subject<AppStatus>();
  appLoaded = this._appLoadingSubject.asObservable();

  currentAppStatus = AppStatus.Initialized;

  constructor(
    private _userRoleService: UserRoleService,
    private _router: Router,
    private _util: PpsUtilityService,
    private _sessionService: SessionService,
    private _logger: LoggingService
  ) {
    let stsSettings: UserManagerSettings = {
      authority: environment.stsAuthority,
      client_id: environment.clientId,
      redirect_uri: `${environment.clientRoot}signin-callback`,
      scope: "openid profile pps.api",
      response_type: "code",
      post_logout_redirect_uri: `${environment.clientRoot}signout-callback`,
      accessTokenExpiringNotificationTime: 360, //seconds
      automaticSilentRenew: false,
      silent_redirect_uri: `${environment.clientRoot}assets/silent-callback.html`,
      includeIdTokenInSilentRenew: false
    };

    //oidc-connect logger
    Log.logger = console;
    //Log.level = Log.DEBUG;

    this._userManager = new UserManager(stsSettings);
    this._userManager.events.addAccessTokenExpiring(_ => {
      this.silentLogin(1, 5);
    });

    this._appLoadingSubject.next(this.currentAppStatus);
  }

  private silentLogin(counter, tryOutTimes){
    this._userManager.signinSilent()
    .then((user) =>{
      this._user = user;
      let expiresAt = new Date(user.expires_at * 1000);
      console.log(this._logger.log("signInSilent succeeded. Next expires at: " + format(expiresAt, "MM/dd/yyyy hh:mm:ss"), "", this.constructor.name, "constructor"));
    })
    .catch((error: Error) =>{
      console.log(error);
      console.log(this._logger.log("signInSilent failed:", error, this.constructor.name, "constructor"));

      if (counter < tryOutTimes){
        console.log("Silent login failed, try again... (Total tryout times: " + (counter + 1) + ")");
        window.setTimeout(() => this.silentLogin(counter + 1, tryOutTimes), 2000);
      }else{
        console.log("Now should log user out after " + counter + " times try out");
        //this.logout();
      }
    });
  }

  isLoggedInPromise(){
    return this._userManager.getUser().then(user => {
      this._user = user;
      let userLoggedIn = this._user !== null && !this._user.expired;
      
      if (userLoggedIn){
        if (this.currentAppStatus !== AppStatus.Ready){
          this.currentAppStatus = AppStatus.Ready;
          this._appLoadingSubject.next(this.currentAppStatus);
          console.log("isLoggedInPromise");
        }
      }

      return userLoggedIn;
    })
    .catch((error: Error) =>{
      return false;
    });
  }

  login(){
    this.currentAppStatus = AppStatus.LoginStarted;
    this._appLoadingSubject.next(this.currentAppStatus);
    return this._userManager.signinRedirect();
  }

  completeLogin(){
    return this._userManager.signinRedirectCallback()
      .then(user => {
        this._user = user;
        this.currentAppStatus = AppStatus.LoginCompleted;
        this._appLoadingSubject.next(this.currentAppStatus);
        console.log(this._logger.log("Login completed", user, this.constructor.name, "completeLogin"));
        return true;
      })
      .then(() => {
        this.currentAppStatus = AppStatus.UserContextLoading;
        this._appLoadingSubject.next(this.currentAppStatus);
        return this.loadUserContext(1, 5);
      })
      .then(userContextLoaded => {
        this.currentAppStatus = AppStatus.Ready;
        this._appLoadingSubject.next(this.currentAppStatus);
        this._loginChangeSubject.next(userContextLoaded);
        return userContextLoaded;
      })
      .catch(error => {
        this._loginChangeSubject.next(false);
        console.log(error);
        console.log(this._logger.log("completeLogin error:", error, this.constructor.name, "completeLogin"));
        return false;
      });
  }

  logout(){
    this.currentAppStatus = AppStatus.LogoutStarted;
    this._appLoadingSubject.next(this.currentAppStatus);
    return this._userManager.signoutRedirect();
  }
  
  completeLogout(){
    this._user = null;
    this._userContext = null;
    this._loginChangeSubject.next(false);
    return this._userManager.signoutRedirectCallback()
      .then(res => {
        this._sessionService.clearUserContext();

        this.currentAppStatus = AppStatus.LogoutCompleted;
        this._appLoadingSubject.next(this.currentAppStatus);
        
        console.log(this._logger.log("User logged out", res, this.constructor.name, "completeLogout"));
      });
  }

  userLoggedIn(){
    return this._userManager.getUser().then(user => {
      const userCurrent = !!user && !user.expired;

      this._user = user;
      if (userCurrent){
        if (this._sessionService.shouldRefreshUserContext()){
          return this.loadUserContext(1, 5);
        }else{
          this._userContext = this._sessionService.getUserContext();
        }
      }

      return userCurrent;
    });
  }

  loadUserContextFromSession(){
    return this._sessionService.getUserContext();
  }
 
  getAccessToken(){
    return this._userManager.getUser()
      .then(user => {
        if (!!user && !user.expired){
          return user.access_token;
        }else{
          return null;
        }
      });
  }

  loadUserContext(counter, tryOutTimes){
    return this._userRoleService.getUserContext()
      .toPromise()
      .then((res) => {
        if (res.succeed){
          this._userContext = res.result;
          this._sessionService.setUserContext(res.result);
          console.log(this._logger.log("UserContext loaded:", this._userContext, this.constructor.name, "loadUserContext"));
          return true;
        }else{
          if (counter <= tryOutTimes){
            console.log(this._logger.log("Load UserContext Failed: try again ... (Total tryout times: (" + counter + "))", res, this.constructor.name, "loadUserContext"));
            return this.loadUserContext(counter + 1, tryOutTimes);
          }
          return false;
        }
      })
      .catch( (error) => {
        if (counter < tryOutTimes){
          console.log(this._logger.log("Load UserContext Exception: try again ... (Total tryout times: (" + counter + "))", error, this.constructor.name, "loadUserContext"));
          return this.loadUserContext(counter + 1, tryOutTimes);
        }
        return false;
      });
  }

  canUserDoAllOfTheOperartions(operations: BusinessOperation[]):boolean{
    var userOperations = this._userContext && this._userContext.userOperations ? 
      this._userContext.userOperations.map(uo => uo.id as BusinessOperation) : [];

    for(var i = 0; i < operations.length; i ++ ){
      if (!userOperations.includes(operations[i])){
        return false;
      }
    }

    return true;
  }

  canUserDoAnyOfTheOperartions(operations: BusinessOperation[]):boolean{
    var userOperations = this._userContext && this._userContext.userOperations ? 
      this._userContext.userOperations.map(uo => uo.id as BusinessOperation) : [];

    for(var i = 0; i < operations.length; i ++ ){
      if (userOperations.includes(operations[i])){
        return true;
      }
    }

    return false;
  }

  canUserDoOperartion(operation: BusinessOperation):boolean{
    var userOperations = this._userContext && this._userContext.userOperations ? 
      this._userContext.userOperations.map(uo => uo.id as BusinessOperation) : [];
    return userOperations.includes(operation);
  }

  get isUserAdmin(): boolean{
    return this._userContext && this._userContext.isAdmin;
  }
 
  get isUserSysAdmin(): boolean{
    return this._userContext && this._userContext.isSysAdmin;
  }

  get userContext(): UserContext{
    if (this._userContext && this._userContext.zoesenUser){
      return this._userContext;
    }

    var userCtx = new UserContext();
    userCtx.zoesenUser = {
      id: 0,
      zoesenUserId: "",
      zoesenUsername: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      avatar: ""
    };

    return userCtx;
  }

  // get isLoggedIn(): boolean{
  //   const userCurrent = !!this._user && !this._user.expired;
  //   return userCurrent && this._userContext !== null;
  // }

  get userFullname(){
    if (this._userContext && this._userContext.zoesenUser){
      return this._util.getEmployeeName(this._userContext.zoesenUser);
    }

    return "";
  }

  setUserProfile(profile){
    if (this._userContext && this._userContext.zoesenUser){
      this.userContext.zoesenUser.firstName = profile.firstName;
      this.userContext.zoesenUser.lastName = profile.lastName;
      this.userContext.zoesenUser.email = profile.email;
    }
  }
}
